import { useCallback, useEffect, useRef } from 'react';
import { signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';

export interface RecallUpdatedMessage {
    id: string;
    recallStatus: {
        typeName: string;
        value: number;
    };
    reference: string;
    accountName: string;
    dateRequested: string | Date | null;
    requestType: string;
    requestedBy: string;
    createdBy?: string;
    onBehalfOf?: string;
    deliveryMethod?: string;
    deliveryPrice?: string;
    orderNumber: string;
    invoiceAddress: any;
    deliveryAddress?: any;
    sendToEmail?: string;
    courier?: string;
    packaging?: string;
    trackingUrl?: string;
    trackingCode?: string;
    itemCount: number;
    cancellationNotes?: string;
    canEdit: boolean;
    notifyAsNew?: boolean;
}

export const recallUpdatedSignal = signal<RecallUpdatedMessage | undefined>(undefined);

// Track newly added recall IDs for fade-in animation
// This is used to smoothly fade out the "new" state after the configured time period
export const newRecallIdsSignal = signal<Set<string>>(new Set());

const DEFAULT_NEW_RECALL_FADE_OUT_DELAY_MS = 600000; // 10 minutes (fallback if backend value not provided)

export interface UseRecallNotificationsOptions {
    currentData: any[] | undefined;
    onDataUpdate: (updatedData: any[]) => void;
    sortByDate?: boolean; // If true, sorts by DateRequested. If false, appends to end of list. Default: true
    isNewTimePeriodMinutes?: number; // Time period in minutes from backend API response
}

export default function useRecallNotifications({ currentData, onDataUpdate, sortByDate = true, isNewTimePeriodMinutes }: UseRecallNotificationsOptions) {
    // Convert minutes to milliseconds, with fallback to default
    const fadeOutDelayMs = isNewTimePeriodMinutes ? isNewTimePeriodMinutes * 60 * 1000 : DEFAULT_NEW_RECALL_FADE_OUT_DELAY_MS;
    useSignals();
    const dataRef = useRef(currentData);
    const onDataUpdateRef = useRef(onDataUpdate);
    const sortByDateRef = useRef(sortByDate);
    const fadeOutDelayMsRef = useRef(fadeOutDelayMs);
    
    // Keep fadeOutDelayMsRef in sync with the current value
    useEffect(() => {
        fadeOutDelayMsRef.current = fadeOutDelayMs;
    }, [fadeOutDelayMs]);

    // Keep refs in sync
    useEffect(() => {
        dataRef.current = currentData;
        
        // When data is initially loaded, check for items with notifyAsNew and add them to tracking
        // Also remove from tracking any items that the backend says are no longer new
        if (currentData && Array.isArray(currentData)) {
            const currentNewIds = new Set(newRecallIdsSignal.value);
            let hasChanges = false;
            
            currentData.forEach((item: any) => {
                if (item.notifyAsNew && !currentNewIds.has(item.id)) {
                    currentNewIds.add(item.id);
                    hasChanges = true;
                    
                    // Remove the marker after the configured time period
                    setTimeout(() => {
                        const updatedNewIds = new Set(newRecallIdsSignal.value);
                        updatedNewIds.delete(item.id);
                        newRecallIdsSignal.value = new Set(updatedNewIds);
                    }, fadeOutDelayMsRef.current);
                } else if (item.notifyAsNew === false && currentNewIds.has(item.id)) {
                    currentNewIds.delete(item.id);
                    hasChanges = true;
                }
            });
            
            if (hasChanges) {
                newRecallIdsSignal.value = new Set(currentNewIds);
            }
        }
    }, [currentData]);

    useEffect(() => {
        onDataUpdateRef.current = onDataUpdate;
    }, [onDataUpdate]);

    useEffect(() => {
        sortByDateRef.current = sortByDate;
    }, [sortByDate]);

    const mergeRecallUpdate = useCallback((message: RecallUpdatedMessage) => {
        const currentList = dataRef.current;
        // If no current data, initialize with just the new recall
        if (!currentList || !Array.isArray(currentList)) {
            onDataUpdateRef.current([message]);
            
            // Track this as a new recall for animation if notifyAsNew is true
            if (message.notifyAsNew) {
                const currentNewIds = new Set(newRecallIdsSignal.value);
                currentNewIds.add(message.id);
                newRecallIdsSignal.value = new Set(currentNewIds);
                
                // Remove the marker after the configured time period
                setTimeout(() => {
                    const updatedNewIds = new Set(newRecallIdsSignal.value);
                    updatedNewIds.delete(message.id);
                    newRecallIdsSignal.value = new Set(updatedNewIds);
                }, fadeOutDelayMsRef.current);
            }
            return;
        }

        // Check if this recall already exists in the list
        const existingIndex = currentList.findIndex((item: any) => item.id === message.id);
        const isNewRecall = existingIndex === -1;

        let updatedList: any[];

        if (isNewRecall) {
            // Add new recall to the list
            // If sortByDate is false, add to start of list, otherwise add to end (will be sorted)
            if (sortByDateRef.current) {
                updatedList = [...currentList, message];
            } else {
                updatedList = [message, ...currentList];
            }
            
            // Track this as a new recall for animation if notifyAsNew is true
            if (message.notifyAsNew) {
                const currentNewIds = new Set(newRecallIdsSignal.value);
                currentNewIds.add(message.id);
                newRecallIdsSignal.value = new Set(currentNewIds);
                
                // Remove the marker after the configured time period
                setTimeout(() => {
                    const updatedNewIds = new Set(newRecallIdsSignal.value);
                    updatedNewIds.delete(message.id);
                    newRecallIdsSignal.value = new Set(updatedNewIds);
                }, fadeOutDelayMsRef.current);
            }
        } else {
            // Update existing recall with key-value patch
            // Preserve notifyAsNew from the message if provided, otherwise keep existing value
            updatedList = [...currentList];
            const existingItem = updatedList[existingIndex];
            const { notifyAsNew: messageNotifyAsNew, ...messageWithoutNotifyAsNew } = message;
            const finalNotifyAsNew = messageNotifyAsNew !== undefined ? messageNotifyAsNew : existingItem.notifyAsNew;
            
            updatedList[existingIndex] = {
                ...existingItem,
                ...messageWithoutNotifyAsNew,
                notifyAsNew: finalNotifyAsNew
            };
            
            // Track this as a new recall for animation if notifyAsNew is true
            if (finalNotifyAsNew) {
                const currentNewIds = new Set(newRecallIdsSignal.value);
                if (!currentNewIds.has(message.id)) {
                    currentNewIds.add(message.id);
                    newRecallIdsSignal.value = new Set(currentNewIds);
                    
                    // Remove the marker after the configured time period
                    setTimeout(() => {
                        const updatedNewIds = new Set(newRecallIdsSignal.value);
                        updatedNewIds.delete(message.id);
                        newRecallIdsSignal.value = new Set(updatedNewIds);
                    }, fadeOutDelayMsRef.current);
                }
            } else {
                // Backend says no longer new – remove from tracking so UI stops showing the tag
                if (newRecallIdsSignal.value.has(message.id)) {
                    const currentNewIds = new Set(newRecallIdsSignal.value);
                    currentNewIds.delete(message.id);
                    newRecallIdsSignal.value = new Set(currentNewIds);
                }
            }
        }

        onDataUpdateRef.current(updatedList);
    }, []);

    // Subscribe to recall updates
    useEffect(() => {
        const unsubscribe = recallUpdatedSignal.subscribe((message) => {
            if (message) {
                // Process the message
                try {
                    mergeRecallUpdate(message);
                } catch (error) {
                    console.error('Error processing recall update:', error);
                }
                
                // Clear the signal after a delay to ensure all subscribers have processed it
                // This is critical for SignalR backplane scenarios in Azure where:
                // 1. Messages may arrive with slight timing differences
                // 2. Multiple hook instances may need to process the same message
                // 3. React re-renders might cause subscriptions to be temporarily inactive
                // The delay ensures the signal value persists long enough for all subscribers
                setTimeout(() => {
                    // Only clear if the signal still contains this message (hasn't been overwritten)
                    if (recallUpdatedSignal.value?.id === message.id) {
                        recallUpdatedSignal.value = undefined;
                    }
                }, 200);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [mergeRecallUpdate]);

    return {
        newRecallIds: newRecallIdsSignal.value // Expose signal value to trigger re-renders
    };
}
